import React from 'react';

/** Parse WhatsApp-style inline formatting for preview (*bold*, _italic_). */
export function renderWhatsAppFormattedText(text: string): React.ReactNode[] {
  if (!text) return [];

  const nodes: React.ReactNode[] = [];
  const pattern = /(\*[^*\n]+\*|_[^_\n]+_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) {
      nodes.push(<React.Fragment key={key++}>{text.slice(lastIndex, match.index)}</React.Fragment>);
    }
    const token = match[0];
    const inner = token.slice(1, -1);
    if (token.startsWith('*')) {
      nodes.push(<strong key={key++}>{inner}</strong>);
    } else {
      nodes.push(<em key={key++}>{inner}</em>);
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(<React.Fragment key={key++}>{text.slice(lastIndex)}</React.Fragment>);
  }

  return nodes.length ? nodes : [text];
}

export function whatsappMarkupToHtml(text: string): string {
  if (!text) return '';

  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const pattern = /(\*[^*\n]+\*|_[^_\n]+_)/g;
  let lastIndex = 0;
  let html = '';
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    html += escapeHtml(text.slice(lastIndex, match.index)).replace(/\n/g, '<br>');
    const token = match[0];
    const inner = escapeHtml(token.slice(1, -1));
    html += token.startsWith('*') ? `<strong>${inner}</strong>` : `<em>${inner}</em>`;
    lastIndex = pattern.lastIndex;
  }

  html += escapeHtml(text.slice(lastIndex)).replace(/\n/g, '<br>');
  return html;
}

function inlineMarkupFromNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') return '\n';

  const inner = Array.from(el.childNodes).map(inlineMarkupFromNode).join('');

  if (tag === 'strong' || tag === 'b') return inner ? `*${inner}*` : '';
  if (tag === 'em' || tag === 'i') return inner ? `_${inner}_` : '';

  return inner;
}

function isBlockElement(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  return tag === 'div' || tag === 'p';
}

export function htmlToWhatsappMarkup(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;

  const lines: string[] = [];
  let inlineBuffer: Node[] = [];

  const flushInline = () => {
    if (!inlineBuffer.length) return;
    lines.push(inlineBuffer.map(inlineMarkupFromNode).join(''));
    inlineBuffer = [];
  };

  for (const child of Array.from(container.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE && isBlockElement(child as HTMLElement)) {
      flushInline();
      lines.push(inlineMarkupFromNode(child).replace(/\n+$/, ''));
      continue;
    }

    if (child.nodeType === Node.TEXT_NODE && !(child.textContent || '').length) {
      continue;
    }

    inlineBuffer.push(child);
  }

  flushInline();

  return lines.join('\n').replace(/\n+$/, '');
}

export function wrapTextareaSelection(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  wrapper: string
): { value: string; selectionStart: number; selectionEnd: number } {
  const before = value.slice(0, selectionStart);
  const selected = value.slice(selectionStart, selectionEnd);
  const after = value.slice(selectionEnd);
  const wrapped = `${wrapper}${selected || 'text'}${wrapper}`;
  const nextValue = `${before}${wrapped}${after}`;
  const start = before.length + wrapper.length;
  const end = start + (selected || 'text').length;
  return { value: nextValue, selectionStart: start, selectionEnd: end };
}

export function insertAtTextareaCursor(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insertText: string
): { value: string; cursor: number } {
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const nextValue = `${before}${insertText}${after}`;
  const cursor = before.length + insertText.length;
  return { value: nextValue, cursor };
}

export const COMMON_WHATSAPP_EMOJIS = [
  '😀', '😊', '🙂', '😉', '😍', '🥰', '😘', '👍', '👏', '🙏',
  '❤️', '💚', '💙', '✅', '⭐', '🎉', '🔔', '📅', '🏥', '🚑',
  '💊', '🩺', '📞', '📍', '⏰', '✨', '💪', '🤝', '🙌', '😢',
];

/** Detect unclosed * or _ formatting (saved as-is to Interakt/Meta). */
export function findUnpairedWhatsAppFormatMarkers(text: string): string | null {
  const value = String(text || '');
  for (const marker of ['*', '_'] as const) {
    let open = false;
    for (let i = 0; i < value.length; i += 1) {
      if (value[i] !== marker) continue;
      if (value[i - 1] === '\\') continue;
      open = !open;
    }
    if (open) {
      return `Unclosed ${marker === '*' ? 'bold (*)' : 'italic (_)'} marker — Interakt will show a literal "${marker}" in the message (e.g. "link${marker}to"). Remove formatting or close the pair.`;
    }
  }
  return null;
}

/** True when body uses WhatsApp inline formatting. */
export function hasWhatsAppInlineFormatting(text: string): boolean {
  return /(\*[^*\n]+\*|_[^_\n]+_)/.test(String(text || ''));
}
